package com.swetdot.swetgram.ui.moderation

import androidx.compose.runtime.saveable.Saver
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.api.ApiError
import com.swetdot.swetgram.data.moderation.REPORT_DETAILS_MAX
import com.swetdot.swetgram.data.moderation.ReportReason
import com.swetdot.swetgram.data.moderation.ReportSubmitter
import com.swetdot.swetgram.data.moderation.ReportTarget
import com.swetdot.swetgram.data.moderation.ReportTargetKind
import com.swetdot.swetgram.domain.UserId
import com.swetdot.swetgram.ui.profile.profileMessage
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

/*
 * SPS §16.3's report flow: ONE implementation, used by all five of the surfaces the spec names.
 *
 *
 * WHY ONE, AND NOT ONE PER SCREEN
 *
 * SPS §16.3 lists five reportable things -- users, messages, Discover posts, Discover comments and
 * groups -- and they are spread across five screens owned by four packages. A report dialog per
 * screen would be five copies of a seven-item reason list, five confirmations, and five different
 * answers to "what happens now", drifting from the first day. That is exactly the argument
 * `ChatDialog` makes about action sheets ("there were two before it and the paddings had already
 * drifted") and `SwetListRow` makes about rows, applied to the one surface where drift is not
 * cosmetic: a person is telling us somebody is hurting them, and the app must say the same true
 * thing every time.
 *
 * So the screens raise a [ReportRequest] and know nothing else. This file owns the reasons, the
 * confirmation, the network call, the copy and the receipt.
 *
 *
 * WHAT THE FOLDER SAYS, AND WHERE IT STOPS
 *
 *  * SPS §16.3 gives the five targets and the seven reasons and nothing about presentation.
 *  * SPS §16.2 keeps BLOCKING as a separate act in a separate subsection.
 *  * SDS §11.1 puts "Report user" under the Destructive Button and requires confirmation dialogs
 *    for destructive actions.
 *  * SDS §12.5 puts Report in the message long-press menu and adds the one placement rule anywhere
 *    in the folder: "Report should be available for messages not sent by the current user."
 *  * SDS §13 lists a "More/report action" in the Discover post anatomy and Report among its actions.
 *  * SDS is SILENT on reporting a group, on a report's confirmation copy, and on what a reporter is
 *    told afterwards. Everything in this file about those three is a DECISION, marked as such.
 *
 *
 * THE CONFIRMATION IS NEW, AND IT REVERSES AN EARLIER CALL
 *
 * `PublicProfileScreen`'s first report dialog said, in as many words, "There is no 'are you sure'
 * step. Reporting is not destructive." That is now argued the other way, on facts about the
 * contract rather than taste:
 *
 *  * A REPORT CANNOT BE WITHDRAWN. v1.0.0 has no `deleteReport` and no `updateReport`; the only
 *    operations that touch a report after it exists are the two under `/admin`. A mis-tap is
 *    permanent and it is permanent in somebody else's queue.
 *  * IT COSTS A METERED SLOT. Reports are limited to 30 per day per account and the limit is spent
 *    on every attempt, including duplicates and unresolvable targets. An accidental report is one
 *    fewer real one.
 *  * IT NAMES A PERSON. SDS §11.1 classes "Report user" as destructive and asks for exactly this.
 *
 * The friction is one tap on a step that also SHOWS the reason chosen, which is the step where a
 * mis-tapped "Illegal content" gets caught.
 */

// ---------------------------------------------------------------------------------------------
// What a screen raises
// ---------------------------------------------------------------------------------------------

/**
 * The account behind reported content, so the receipt can offer to block them.
 *
 * [username] rather than only an id because blocking is offered NEXT TO the person's handle -- "you
 * can also block @ada" -- and because every surface that can raise a report already holds a
 * `domain.User`, which carries both.
 */
data class ReportedAccount(
    val userId: UserId,
    val username: String,
    val displayName: String,
)

/**
 * One thing a user has asked to report.
 *
 * ONE PARAMETER RATHER THAN THREE on every screen's callback, so that adding a field later does not
 * touch five call sites.
 *
 * [account] is null exactly when there is no single account behind the target -- which today means
 * a group, and only a group. For a message, a post or a comment it is the AUTHOR, not the reporter;
 * for a person it is the person. It is what makes the block offer possible, and its absence is what
 * suppresses it.
 */
data class ReportRequest(
    val target: ReportTarget,
    val account: ReportedAccount? = null,
    /** A group's name, for the title. Ignored for every other target kind. */
    val groupName: String? = null,
)

// ---------------------------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------------------------

/** Whether the "block them too" offer on the receipt has been taken, and how it went. */
sealed interface ReportBlockState {
    /** Offered and not yet taken. */
    data object Available : ReportBlockState

    data object InFlight : ReportBlockState

    data object Done : ReportBlockState

    data class Failed(val message: String) : ReportBlockState
}

sealed interface ReportPhase {
    /** The sheet is open and the user is choosing. */
    data object Composing : ReportPhase

    data object Sending : ReportPhase

    /**
     * The server has it.
     *
     * [alreadyInReview] is the only claim the client can make about WHICH report came back -- see
     * `ReportReceipt.alreadyInReview` for why a fresher inference was refused.
     */
    data class Sent(
        val alreadyInReview: Boolean,
        val block: ReportBlockState = ReportBlockState.Available,
    ) : ReportPhase

    /**
     * [retryable] is false when trying again cannot possibly work: the target is gone, the daily
     * limit is spent, the session has ended. The sheet then offers Close and not Try again, because
     * a retry button over a permanent refusal is a button that exists to fail.
     */
    data class Failed(val message: String, val retryable: Boolean) : ReportPhase
}

/** Null [request] means the sheet is closed. Nothing else about it is meaningful then. */
data class ReportFlowState(
    val request: ReportRequest? = null,
    val phase: ReportPhase = ReportPhase.Composing,
)

// ---------------------------------------------------------------------------------------------
// The view model
// ---------------------------------------------------------------------------------------------

/**
 * Blocking, as this flow needs it.
 *
 * A one-method interface rather than `BlockRepository`, for the reason `DiscoverPostDetailSource`
 * is one: the concrete repository needs an `ApiClient`, and a rule that needs a network stack to
 * exercise is a rule whose corners never get tested. Null in the constructor means the offer is
 * simply not made.
 */
fun interface AccountBlocker {
    suspend fun block(userId: UserId)
}

/**
 * ONE instance for the whole app, created where the navigation host is, so a report survives the
 * screen it was raised from.
 *
 * That placement is deliberate. A `rememberCoroutineScope` inside the sheet would cancel the POST
 * the moment the dialog left composition -- and the composition it lives in is the screen the user
 * just reported somebody on, which is a screen people leave. `viewModelScope` does not.
 *
 * [reports] is nullable and null is a real state: reporting is not wired on this build, every
 * surface hides its Report action, and this view model never opens. A Report control that silently
 * does nothing is worse than none at all on a safety screen -- the user believes they have been
 * heard.
 */
class ReportViewModel(
    private val reports: ReportSubmitter?,
    private val blocks: AccountBlocker? = null,
) : ViewModel() {

    private val _state = MutableStateFlow(ReportFlowState())
    val state: StateFlow<ReportFlowState> = _state.asStateFlow()

    /** False when nothing can send a report; every surface asks this before drawing its action. */
    val canReport: Boolean get() = reports != null

    /**
     * Opens the sheet.
     *
     * Ignored while a send is in flight, so a stray tap behind the dialog cannot swap the target
     * out from under a request that is already on its way to the server.
     */
    fun open(request: ReportRequest) {
        if (reports == null) return
        if (_state.value.phase == ReportPhase.Sending) return
        _state.value = ReportFlowState(request = request, phase = ReportPhase.Composing)
    }

    /**
     * Sends it. Never retried automatically -- see `ReportRepository`: the operation carries no
     * `Idempotency-Key`, so nothing below this can safely repeat the POST.
     */
    fun submit(reason: ReportReason, details: String?) {
        val submitter = reports ?: return
        val current = _state.value
        val request = current.request ?: return
        if (current.phase == ReportPhase.Sending) return

        _state.value = current.copy(phase = ReportPhase.Sending)
        viewModelScope.launch {
            try {
                val receipt = submitter.submit(
                    target = request.target,
                    reason = reason,
                    // Blank becomes null rather than "": an untouched box must not arrive in a
                    // moderator's queue as an empty line that reads like a deleted note.
                    details = details?.trim()?.takeIf { it.isNotEmpty() },
                )
                _state.value = _state.value.copy(
                    phase = ReportPhase.Sent(alreadyInReview = receipt.alreadyInReview),
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    phase = ReportPhase.Failed(
                        message = reportFailureMessage(error, request.target.kind),
                        retryable = reportFailureIsRetryable(error),
                    ),
                )
            }
        }
    }

    /**
     * The other half of SPS §16 -- §16.2's block, offered from §16.3's receipt.
     *
     * NOT OPTIMISTIC. `BlockedUsersViewModel` records the rule: removing a block early is the safe
     * direction and adding one early is not, because showing somebody as blocked while they can
     * still write is the one failure a safety control must never have.
     */
    fun blockAccount() {
        val blocker = blocks ?: return
        val current = _state.value
        val account = current.request?.account ?: return
        val sent = current.phase as? ReportPhase.Sent ?: return
        if (sent.block == ReportBlockState.InFlight || sent.block == ReportBlockState.Done) return

        _state.value = current.copy(phase = sent.copy(block = ReportBlockState.InFlight))
        viewModelScope.launch {
            try {
                blocker.block(account.userId)
                _state.value = _state.value.updateSent { it.copy(block = ReportBlockState.Done) }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.updateSent {
                    it.copy(
                        block = ReportBlockState.Failed(
                            error.profileMessage("Could not block that account."),
                        ),
                    )
                }
            }
        }
    }

    /** Back to the form after a retryable failure, keeping the same target. */
    fun retry() {
        val current = _state.value
        if (current.request == null) return
        if (current.phase !is ReportPhase.Failed) return
        _state.value = current.copy(phase = ReportPhase.Composing)
    }

    /**
     * Closes the sheet.
     *
     * Refused while the send is in flight: dismissing then would leave the user with no idea
     * whether a report they cannot withdraw was filed. The dialog is not cancellable in that state
     * either -- see `ReportSheet`.
     */
    fun dismiss() {
        if (_state.value.phase == ReportPhase.Sending) return
        _state.value = ReportFlowState()
    }

    private fun ReportFlowState.updateSent(
        block: (ReportPhase.Sent) -> ReportPhase.Sent,
    ): ReportFlowState {
        val sent = phase as? ReportPhase.Sent ?: return this
        return copy(phase = block(sent))
    }
}

// ---------------------------------------------------------------------------------------------
// Copy and rules -- pure, so they can be exercised without a renderer or a network
// ---------------------------------------------------------------------------------------------

/*
 * TODO(localization): every string below moves into resources for EN + RU. They are functions
 * rather than templates for the reason `commentCountLabel` gives -- a sentence that varies by
 * target kind does not survive translation as string concatenation.
 *
 * BLOCKED, NOT FORGOTTEN, and on something specific. The moderation surface's copy already has a
 * home -- the `moderation_` prefix in `values/strings.xml` and `values-ru/strings.xml`, where
 * `moderation_confirm_*` and `moderation_action_desc_*` already live. Those two files are shared
 * across every area of the app and are not split by feature the way `strings_chat.xml` and
 * `strings_profile.xml` are, so the localisation passes that ran one area at a time each in its own
 * file could not write here without colliding. The work is a file-ownership decision away, not a
 * design one.
 *
 * AND THE SENTENCES BELOW ARE THE HARDEST IN THE APP TO MOVE, which is the other half of why they
 * are still here. `reportConfirmBody` and `reportReceiptBody` each splice `targetNoun` into the
 * middle of a sentence: "this post stays where it is", "we could not find that comment any more".
 * Russian needs those nouns in the accusative and the genitive respectively, and a resource per
 * (sentence x target kind) is five keys per sentence, not one with an argument. That is the right
 * answer and it is a rewrite, not a substitution -- so it belongs to a pass that owns both this
 * file and the resource file, and can be reviewed as one change.
 */

/** SPS §16.3's seven, in the spec's own order, matching the contract's enum one for one. */
val ReportReasonOrder: List<ReportReason> = listOf(
    ReportReason.SPAM,
    ReportReason.SCAM,
    ReportReason.HARASSMENT,
    ReportReason.HATE_ABUSE,
    ReportReason.IMPERSONATION,
    ReportReason.ILLEGAL_CONTENT,
    ReportReason.OTHER,
)

fun reasonLabel(reason: ReportReason): String = when (reason) {
    ReportReason.SPAM -> "Spam"
    ReportReason.SCAM -> "Scam"
    ReportReason.HARASSMENT -> "Harassment"
    ReportReason.HATE_ABUSE -> "Hate or abusive content"
    ReportReason.IMPERSONATION -> "Impersonation"
    ReportReason.ILLEGAL_CONTENT -> "Illegal content"
    // "Something else" rather than "Other", which reads as a form field. SDS §18.1: human language.
    ReportReason.OTHER -> "Something else"
}

/** The word for what is being reported, used in half a dozen sentences below. */
internal fun targetNoun(kind: ReportTargetKind): String = when (kind) {
    ReportTargetKind.PERSON -> "account"
    ReportTargetKind.MESSAGE -> "message"
    ReportTargetKind.DISCOVER_POST -> "post"
    ReportTargetKind.DISCOVER_COMMENT -> "comment"
    ReportTargetKind.GROUP -> "group"
    ReportTargetKind.PICSO_POST -> "photo"
}

/**
 * The sheet's title.
 *
 * A PERSON and a GROUP are named; a message, a post and a comment are not. That asymmetry is
 * deliberate rather than an oversight: naming the author in the title of a report about one message
 * ("Report Ada Lovelace") would quietly widen what the user thinks they are doing, from "this one
 * message" to "this person". The contract keeps them apart -- `target_type` decides which table the
 * server resolves against -- and the title should too.
 */
fun reportTitle(request: ReportRequest): String = when (request.target.kind) {
    ReportTargetKind.PERSON ->
        request.account?.displayName?.takeIf { it.isNotBlank() }
            ?.let { "Report $it" }
            ?: "Report this account"
    ReportTargetKind.GROUP ->
        request.groupName?.takeIf { it.isNotBlank() }
            ?.let { "Report $it" }
            ?: "Report this group"
    else -> "Report this ${targetNoun(request.target.kind)}"
}

/**
 * The line under the title, on the reason step.
 *
 * It states the two facts a reporter most often gets wrong, before they choose anything: the report
 * is not shown to the person, and choosing a reason does not remove anything.
 */
fun reportIntro(kind: ReportTargetKind): String = when (kind) {
    ReportTargetKind.GROUP ->
        "Tell us what is wrong with this group. Your report is not shown to its members."
    ReportTargetKind.PERSON ->
        "Tell us what is wrong. Your report is not shown to this account."
    else ->
        "Tell us what is wrong with this ${targetNoun(kind)}. Your report is not shown to whoever " +
            "posted it."
}

/**
 * The confirmation step's body -- the one place the whole truth is told BEFORE anything happens.
 *
 * Four sentences, each of which is a fact the contract or the server actually guarantees:
 *  1. what is sent (the reason, and the details if any);
 *  2. that a person reads it, and that it takes time -- there is no automated removal anywhere;
 *  3. that NOTHING is removed by reporting -- the content stays exactly where it is;
 *  4. that it cannot be taken back, because v1.0.0 has no operation that withdraws a report.
 *
 * Sentence 3 is the one the brief for this change insisted on and the one every messenger gets
 * wrong. `createReport` returns a `Report` with `status: open`; it does not touch the content, and
 * a reporter who closes the sheet expecting the post to be gone will come back angrier than they
 * left.
 */
fun reportConfirmBody(kind: ReportTargetKind, reason: ReportReason, hasDetails: Boolean): String {
    val what = if (hasDetails) {
        "We will send \"${reasonLabel(reason)}\" and what you wrote."
    } else {
        "We will send \"${reasonLabel(reason)}\"."
    }
    // An EM DASH, not the ASCII `--` this codebase uses in comments. SDS §1.1 positions the brand
    // as premium minimalism and §18.2 sets the writing rules; a source-code dash convention showing
    // through in the one dialog a user is asked to read carefully before an irreversible action is
    // the detail that reads as unfinished.
    return "$what A moderator will read it, which can take a while. Nothing is removed by " +
        "reporting — this ${targetNoun(kind)} stays where it is until someone decides. You cannot " +
        "take a report back."
}

/**
 * The receipt.
 *
 * [alreadyInReview] is true only when the server handed back a report in `reviewing`, which a fresh
 * insert can never be -- so it is a fact and not a guess. It is worth its own sentence because it is
 * the difference between "nothing has happened yet" and "a person is looking at this now".
 *
 * NOTE WHAT IS NOT PROMISED: no outcome, no notification, no timescale. There is no operation in
 * v1.0.0 that lets a reporter read their own report back -- the only two read operations are under
 * `/admin` and both answer 403 to an ordinary account -- so the app genuinely cannot tell them what
 * happened, and says so instead of implying a follow-up that will never arrive.
 */
fun reportReceiptBody(kind: ReportTargetKind, alreadyInReview: Boolean): String {
    val head = if (alreadyInReview) {
        "A moderator is already looking at this ${targetNoun(kind)}."
    } else {
        "A moderator will look at this ${targetNoun(kind)}."
    }
    return "$head Nothing has been removed, and we will not be able to tell you what was decided."
}

/**
 * The sentence that makes SPS §16.2 and §16.3 legible as two different acts, shown beside the block
 * offer on the receipt.
 *
 * DECISION -- the folder puts blocking and reporting in adjacent subsections of §16 and never says
 * how they relate. Two things are worth the reader's attention, and both are true of this build:
 * a block is IMMEDIATE and it is THEIRS, whereas a report is a request to someone else that takes
 * time and applies to everybody.
 */
fun blockOfferBody(displayName: String): String =
    "Blocking is different: it happens now, it only affects you, and $displayName is not told. " +
        "They will not be able to send you new messages."

/**
 * The banner text for a failed report.
 *
 * BRANCHES ON `ApiError`, which is the boundary's own type, so no view file compares an error code
 * or imports `protocol` (IMPLEMENTATION_NOTES §10).
 *
 * THE 404 IS THE INTERESTING ONE and it is deliberately vague, because the server made it so.
 * `createReport` answers ONE `report_target_not_found` for every way a target can fail to resolve:
 * it never existed, it has been deleted, it is in a group the reporter is not in, its author has
 * blocked them, it predates their joining. Those are indistinguishable on purpose -- otherwise a
 * type plus a guessed uuid is a live existence probe for private content. So this sentence must not
 * say "it was deleted", which is a claim, and must not say "you cannot see it", which is the leak.
 * It says only that we could not find it any more.
 *
 * The 429 is not a fault. Reports are metered at 30/day and the limit is spent on every attempt,
 * so this is a normal outcome with a normal sentence rather than an error to apologise for.
 */
fun reportFailureMessage(error: Throwable, kind: ReportTargetKind): String = when {
    error is ApiError.NotFound ->
        "We could not find that ${targetNoun(kind)} any more, so there was nothing to report."
    error is ApiError.RateLimited ->
        "You have sent a lot of reports today. Try again tomorrow."
    error is ApiError.Validation ->
        "We could not send that report. Check the details and try again."
    else -> error.profileMessage("Could not send the report. Please try again.")
}

/**
 * Whether the sheet offers Try again.
 *
 * A 404 is terminal: the target will not come back. A 429 is terminal for the rest of the day and
 * `Retry-After` on a daily bucket is not a wait anybody sits through. A validation failure means
 * the body was wrong and repeating it unchanged fails identically. Everything else -- offline, a
 * timeout, a 5xx -- is worth another tap.
 */
fun reportFailureIsRetryable(error: Throwable): Boolean = when (error) {
    is ApiError.NotFound, is ApiError.RateLimited, is ApiError.Validation -> false
    is ApiError.SessionExpired, is ApiError.UpgradeRequired -> false
    else -> true
}

/**
 * Whether the details box is over the contract's cap.
 *
 * COUNTED IN CODE POINTS, not UTF-16 units, because JSON Schema's `maxLength` and Postgres'
 * `char_length` both count code points -- so 1000 emoji is 1000 characters to the server and 2000
 * to `String.length`. `PostDetailViewModel.codePointLength` argues the same measurement for the
 * Discover composer; this reuses the reasoning rather than the private function, which lives in
 * another feature package.
 *
 * TRIMMED FIRST, because a details box holding nothing but spaces is nothing.
 */
fun reportDetailsOverflow(details: String): Int =
    reportDetailsLength(details) - REPORT_DETAILS_MAX

internal fun reportDetailsLength(details: String): Int {
    val text = details.trim()
    var count = 0
    var index = 0
    while (index < text.length) {
        val isPair = text[index].isHighSurrogate() &&
            index + 1 < text.length &&
            text[index + 1].isLowSurrogate()
        index += if (isPair) 2 else 1
        count++
    }
    return count
}

/** Nothing is sent without a reason, and nothing is sent over the cap. */
fun canSendReport(reason: ReportReason?, details: String): Boolean =
    reason != null && reportDetailsOverflow(details) <= 0

// ---------------------------------------------------------------------------------------------
// Surviving a rotation
// ---------------------------------------------------------------------------------------------

/*
 * WHY THESE EXIST NOW, WHEN AN EARLIER PASS ARGUED AGAINST THEM.
 *
 * `ReportSheet` used to carry a note saying its `remember` was a known cost, on two premises:
 * that nothing else in `:shared` used `rememberSaveable`, and that an enum has no saver that is
 * guaranteed on every Compose Multiplatform target. The first has expired -- other surfaces are
 * introducing real usages -- and the second was never true of the shape below. A `Saver` over the
 * enum's own `name` saves a String, which is the most portable saveable there is; nothing here
 * touches `Parcelable`, `Bundle` or any other platform type, so it compiles for iOS unchanged.
 *
 * The cost of NOT having them is specific and it is worst on exactly these two screens: turning
 * the phone mid-report threw away the chosen reason and the typed details, and a report is the one
 * thing in this app a user cannot take back and therefore the one they are most reluctant to start
 * twice. On the moderator screen it threw away a decision, a side effect and a written note.
 *
 * An UNRECOGNISED name restores as "nothing chosen" rather than as a guess. A saved state written
 * by a newer build must never quietly select a different reason than the one the user picked --
 * making them choose again is the safe direction, and it is the only one.
 */

/**
 * A saver for a nullable enum, where null is the real "nothing chosen yet" state.
 *
 * Returning null from `save` tells `rememberSaveable` not to store anything, so restoring falls
 * back to the initial value -- which is null. That is why there is no sentinel string here.
 */
internal fun <T : Enum<T>> optionalEnumSaver(values: List<T>): Saver<T?, String> = Saver(
    save = { it?.name },
    restore = { name -> values.firstOrNull { it.name == name } },
)

/** The same, for a selection that always has a value; [fallback] absorbs an unreadable name. */
internal fun <T : Enum<T>> requiredEnumSaver(values: List<T>, fallback: T): Saver<T, String> = Saver(
    save = { it.name },
    restore = { name -> values.firstOrNull { it.name == name } ?: fallback },
)
